import * as React from 'react';

import { Box, Button, Checkbox, FormControlLabel, Tooltip, Typography } from '@mui/material';
import { FlexBoxColumn, FlexBoxInline, PrimaryBtnSx, RemoveBtnSx, TooltipSlotProps } from '../../@shared/templates';
import { cleanModelDataModalAction, openSchemaDataModalAction } from '../../sagas/modelDataSaga/modelDataSagaActions';
import { useDispatch, useSelector } from 'react-redux';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Palette, Sizes } from '../../@shared/palette';
import ClearIcon from '@mui/icons-material/Clear';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PostAddIcon from '@mui/icons-material/PostAdd';
import { getIsDataCleaning } from '../../reducers/modelData/modelDataReducer';
import { DatasetCleanupType, SchemaApiValue } from '../../@shared/Models/Models';
import { AlertComponent } from '../../components/AlertComponent';
import { GuideStepEnum, NotificationModelType } from '../../reducers/main/mainModel';
import { getGuideStep, setGuideStep } from '../../reducers/main/mainReducer';
import GuideCallout from '../../components/GuideCallout';
import { CollectDataItemModal } from '../../components/CollectDataItemModal';
import moment from 'moment';

export type TabsContentDataBaseProps = {
  schema: SchemaApiValue,
}

export type TabsContentDataProps = TabsContentDataBaseProps & {
  isVisible: boolean
}

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} Bytes`;

  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(2)} KB`;

  return `${(kb / 1024).toFixed(2)} MB`;
}

const TabsContentData = (props: TabsContentDataProps) => {
  const dispatch = useDispatch();
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const [isCollectItemOpen, setIsCollectItemOpen] = React.useState(false);
  const [cleanTrainData, setCleanTrainData] = React.useState<boolean>(true);
  const [cleanTestData, setCleanTestData] = React.useState<boolean>(true);
  const guideStep = useSelector(getGuideStep);

  const isDataCleaning = useSelector(getIsDataCleaning);
  const handleAddData = React.useCallback(() => {
    if (props.schema?.id) {
      dispatch(openSchemaDataModalAction(props.schema.id))
    }
    if(guideStep !== GuideStepEnum.None) {
      dispatch(setGuideStep(GuideStepEnum.FillDatasetModal));
    }
  }, [dispatch, guideStep, props.schema.id]);

  const handleCleanData = React.useCallback(() => {
    if (props.schema?.id) {
      let cleanType = DatasetCleanupType.Both;
      if (cleanTrainData && !cleanTestData) {
        cleanType = DatasetCleanupType.Train;
      }
      else if (!cleanTrainData && cleanTestData) {
        cleanType = DatasetCleanupType.Test;
      }
      dispatch(cleanModelDataModalAction({
        schemaId: props.schema.id,
        cleanType
      }))
    }
  }, [cleanTestData, cleanTrainData, dispatch, props.schema.id]);

  return <div hidden={!props.isVisible}>
    <CollectDataItemModal isOpen={isCollectItemOpen} schema={props.schema} onClose={() => setIsCollectItemOpen(false)} />
    <ConfirmDialog isOpen={isConfirmOpen} onClose={() => setIsConfirmOpen(false)} title='Clean data' description='Data cleaning needs to be confirmed' onConfirmedAction={handleCleanData} isConfirmDisabled={isDataCleaning || (!cleanTrainData && !cleanTestData)} isProcessing={isDataCleaning}>
      {(!cleanTrainData && !cleanTestData) && <Box sx={{marginBottom: "20px"}}>
        <AlertComponent notification={{ message: "Please select at least one dataset to clean.", type: NotificationModelType.Error, date: new Date() }} />
      </Box>}
      <Box sx={{ ...FlexBoxColumn, gap: "10px" }}>
        <FormControlLabel sx={{ margin: 0 }} control={<Checkbox value={cleanTrainData} checked={cleanTrainData} sx={{ padding: 0, marginRight: "10px" }} />} label={<Box>
          <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>Clean train dataset</Typography>
          <Typography sx={{ fontSize: Sizes.textSecondary, color: Palette.textColorSecondary }}>When enabled, the training dataset will be cleaned.</Typography>
        </Box>
        } onChange={(_, v) => setCleanTrainData(v)}
          value={cleanTrainData} />
        <FormControlLabel sx={{ margin: 0 }} control={<Checkbox value={cleanTestData} checked={cleanTestData} sx={{ padding: 0, marginRight: "10px" }} />} label={<Box>
          <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>Clean test dataset</Typography>
          <Typography sx={{ fontSize: Sizes.textSecondary, color: Palette.textColorSecondary }}>When enabled, the test dataset will be cleaned.</Typography>
        </Box>
        } onChange={(_, v) => setCleanTestData(v)}
          value={cleanTestData} />
      </Box>
    </ConfirmDialog>
    <Box sx={{ ...FlexBoxInline, marginBottom: "20px" }}>
      <Tooltip placement="bottom" open={guideStep === GuideStepEnum.ClickDatasetAddBtn} slotProps={TooltipSlotProps} title={
        <GuideCallout>
          Click to open data collection modal. You can add data for training and testing your model. The data should be in the format defined by your schema structure. After adding data you will be able to train your model and make predictions.
        </GuideCallout>
      } arrow>
        <Button sx={PrimaryBtnSx} onClick={handleAddData} startIcon={<PlaylistAddIcon />}>Collect Dataset</Button>
      </Tooltip>
      <Button sx={PrimaryBtnSx} onClick={() => setIsCollectItemOpen(true)} startIcon={<PostAddIcon />}>Collect Item</Button>
      <Button sx={RemoveBtnSx} onClick={() => setIsConfirmOpen(true)} startIcon={<ClearIcon />}>Clean</Button>
    </Box>
    <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary, marginBottom: "5px", display: "block" }} variant="h6">Training dataset length: {props.schema.datasetLength} items</Typography>
    <Typography sx={{ fontSize: Sizes.textSecondary, color: Palette.textColorSecondary, marginBottom: "20px", display: "block" }} variant="caption">The training dataset length refers to the total amount of data used to train a model.</Typography>
    <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary, marginBottom: "5px", display: "block" }} variant="h6">Testing dataset length: {props.schema.testDatasetLength} items</Typography>
    <Typography sx={{ fontSize: Sizes.textSecondary, color: Palette.textColorSecondary, marginBottom: "20px", display: "block" }} variant="caption">The testing dataset length refers to the amount of data set aside to evaluate the performance of a trained model.</Typography>
    <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary, marginBottom: "5px", display: "block" }} variant="h6">Training dataset size: {formatBytes(props.schema.datasetSize)}</Typography>
    <Typography sx={{ fontSize: Sizes.textSecondary, color: Palette.textColorSecondary, marginBottom: "20px", display: "block" }} variant="caption">Amount of memory occupied by the training dataset.</Typography>
    <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary, marginBottom: "5px", display: "block" }} variant="h6">Testing dataset size: {formatBytes(props.schema.testDatasetSize)}</Typography>
    <Typography sx={{ fontSize: Sizes.textSecondary, color: Palette.textColorSecondary, marginBottom: "20px", display: "block" }} variant="caption">Amount of memory occupied by the testing dataset.</Typography>
    <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary, marginBottom: "5px", display: "block" }} variant="h6">Reporting state/last update: {!props.schema.datasetReportingUpdatedDate ? "Not Built Yet" : moment(props.schema.datasetReportingUpdatedDate).format("YYYY-MM-DD HH:mm:ss")}</Typography>
    <Typography sx={{ fontSize: Sizes.textSecondary, color: Palette.textColorSecondary, marginBottom: "20px", display: "block" }} variant="caption">The reporting state indicates whether the dataset reporting has been completed.</Typography>
  </div>
}

export default TabsContentData;
